import axios from "axios";
import { baseApi } from "./baseApi";
import type { MediaDto } from "../../interfaces/media";
import { PlanDto } from "../../interfaces/plan/plan.dto";
import { CreatePlanRequest } from "../../interfaces/plan";
import { UpdatePlanRequest } from "../../interfaces/plan/update-plan-request.interface";

export const planApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPlans: builder.query<PlanDto[], { courseId: string; gradeId: string }>({
      query: ({ courseId, gradeId }) => ({
        url: `/api/plan?courseId=${courseId}&gradeId=${gradeId}`,
      }),
      providesTags: ["Plans"],
    }),
    getPlan: builder.query<PlanDto, string>({
      query: (id) => ({ url: `/api/plan/${id}` }),
      providesTags: ["Plan"],
    }),
    addPlan: builder.mutation<any, CreatePlanRequest>({
      query: (data) => ({
        url: "/api/plan",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Plans"],
    }),
    updatePlan: builder.mutation<any, { id: string; data: UpdatePlanRequest }>({
      query: ({ id, data }) => ({
        url: `/api/plan/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Plans", "Plan"],
    }),
    deletePlan: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/plan/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Plans"],
    }),
    togglePlanState: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/plan/toggle-state/${id}`,
        method: "PATCH",
        body: {},
      }),
      invalidatesTags: ["Plans", "Plan"],
    }),
    getPurchaseablePlansForStudent: builder.query<
      any,
      { courseId: string; gradeId: string; userId: string }
    >({
      query: ({ courseId, gradeId, userId }) => ({
        url: `/api/plan/purchasable/${courseId}/${gradeId}/${userId}`,
      }),
      providesTags: ["PurchasablePlans"],
    }),
    uploadPlanImage: builder.mutation<
      MediaDto,
      {
        file: File;
        localId: number;
        planId?: string;
        onProgress?: (progress: number, localId: number) => void;
      }
    >({
      query: ({ file, localId, planId, onProgress }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: planId
            ? `/api/plan/upload/image?id=${planId}`
            : `/api/plan/upload/image`,
          method: "POST",
          data: formData,
          onUploadProgress: (event: any) => {
            if (event.total && onProgress) {
              const progress = Math.round((event.loaded / event.total) * 100);
              onProgress(progress, localId);
            }
          },
        };
      },
      invalidatesTags: ["Plan"],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetPlanQuery,
  useAddPlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
  useTogglePlanStateMutation,
  useGetPurchaseablePlansForStudentQuery,
  useUploadPlanImageMutation,
} = planApi;
